import React, { useEffect, useState } from "react";
import { Mail, Lock, User, Phone, Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../hooks/useAuth";
import Modal from "../components/_ResponsiveComponents/Modal";
import SEO from "../components/SEO/SEO";
import {
  signInSchema,
  signUpSchema,
  type SignInFormValues,
  type SignUpFormValues,
} from "../schema/auth.schema";

type MessageState = {
  type: "" | "error" | "success";
  text: string;
};

const Auth: React.FC = () => {
  const [isTermsOpen, setTermsOpen] = useState(false);
  const [isPrivacyOpen, setPrivacyOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState<"signin" | "signup">("signin");
  const [showSignInPassword, setShowSignInPassword] = useState(false);
  const [showSignUpPassword, setShowSignUpPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<MessageState>({ type: "", text: "" });
  const navigate = useNavigate();
  const { user } = useAuth();

  const signInForm = useForm<SignInFormValues>({
    resolver: zodResolver(signInSchema),
    defaultValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
  });

  const signUpForm = useForm<SignUpFormValues>({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
      acceptTerms: false,
    },
  });

  useEffect(() => {
    if (user) {
      navigate("/");
    }
  }, [user, navigate]);

  useEffect(() => {
    setMessage({ type: "", text: "" });
  }, [currentPage]);

  const handleSignIn = async (values: SignInFormValues) => {
    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: values.email,
        password: values.password,
      });

      if (error) {
        setMessage({ type: "error", text: error.message });
      } else {
        setMessage({ type: "success", text: "Successfully signed in!" });
        setTimeout(() => navigate("/"), 1000);
      }
    } catch {
      setMessage({ type: "error", text: "An unexpected error occurred" });
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (values: SignUpFormValues) => {
    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const { data, error } = await supabase.auth.signUp({
        email: values.email,
        password: values.password,
        options: {
          data: {
            first_name: values.firstName,
            last_name: values.lastName,
            phone: values.phone,
            full_name: `${values.firstName} ${values.lastName}`,
          },
        },
      });

      if (error) {
        setMessage({ type: "error", text: error.message });
      } else if (data.user && !data.user.email_confirmed_at) {
        setMessage({
          type: "success",
          text: "Please check your email to confirm your account!",
        });
      } else {
        setMessage({ type: "success", text: "Account created successfully!" });
        setTimeout(() => navigate("/"), 1000);
      }
    } catch {
      setMessage({ type: "error", text: "An unexpected error occurred" });
    } finally {
      setLoading(false);
    }
  };

  if (currentPage === "signin") {
    return (
      <>
      <SEO
        title="Sign In"
        description="Sign in to your My Perfume account."
        noindex
      />
      <div className="min-h-screen bg-neutral-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-lg border border-neutral-200 overflow-hidden">
            <div className="bg-white p-8 text-center border-b border-neutral-200">
              <img
                src="/assets/Images/logo/logo.webp"
                alt="My Perfume"
                className="h-16 w-auto mx-auto mb-4"
              />
              <h2 className="text-3xl font-bold text-neutral-900 mb-2">Welcome Back</h2>
              <p className="text-neutral-500">Sign in to your account</p>
            </div>

            <div className="p-8">
              {message.text && (
                <div
                  className={`mb-6 p-4 rounded-lg text-sm font-medium ${
                    message.type === "error"
                      ? "bg-red-50 text-red-700 border border-red-200"
                      : "bg-green-50 text-green-700 border border-green-200"
                  }`}
                >
                  {message.text}
                </div>
              )}

              <form onSubmit={signInForm.handleSubmit(handleSignIn)} className="space-y-6">
                <div>
                  <label
                    htmlFor="signin-email"
                    className="block text-sm font-medium text-neutral-700 mb-2"
                  >
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
                    <input
                      id="signin-email"
                      type="email"
                      {...signInForm.register("email")}
                      autoComplete="username"
                      className="w-full pl-12 pr-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-200 bg-neutral-50 text-neutral-900 placeholder-neutral-400"
                      placeholder="Enter your email"
                    />
                  </div>
                  {signInForm.formState.errors.email && (
                    <p className="mt-2 text-sm text-red-600">
                      {signInForm.formState.errors.email.message}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="signin-password"
                    className="block text-sm font-medium text-neutral-700 mb-2"
                  >
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
                    <input
                      id="signin-password"
                      type={showSignInPassword ? "text" : "password"}
                      {...signInForm.register("password")}
                      autoComplete="current-password"
                      className="w-full pl-12 pr-12 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-200 bg-neutral-50 text-neutral-900 placeholder-neutral-400"
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setShowSignInPassword(!showSignInPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                    >
                      {showSignInPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  {signInForm.formState.errors.password && (
                    <p className="mt-2 text-sm text-red-600">
                      {signInForm.formState.errors.password.message}
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      {...signInForm.register("rememberMe")}
                      className="w-4 h-4 text-accent border-neutral-300 rounded focus:ring-accent bg-white"
                    />
                    <span className="ml-2 text-sm text-neutral-500">Remember me</span>
                  </label>
                  <a
                    href="/forget-password"
                    className="text-sm text-accent hover:text-accent-dark font-medium"
                  >
                    Forgot password?
                  </a>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary text-white py-3 rounded-lg font-semibold hover:bg-primary-light focus:ring-4 focus:ring-primary/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "Signing in..." : "Sign In"}
                </button>
              </form>

              <p className="mt-8 text-center text-sm text-neutral-500">
                Don&apos;t have an account?{" "}
                <button
                  type="button"
                  onClick={() => setCurrentPage("signup")}
                  className="text-accent hover:text-accent-dark font-medium"
                >
                  Sign up
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
      </>
    );
  }

  return (
    <>
    <SEO title="Create Account" description="Create your My Perfume account." noindex />
    <div className="min-h-screen bg-neutral-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-lg border border-neutral-200 overflow-hidden">
          <div className="bg-white p-8 text-center border-b border-neutral-200">
            <img
              src="/assets/Images/logo/logo.webp"
              alt="My Perfume"
              className="mx-auto h-16 w-auto mb-4"
            />
            <h2 className="text-3xl font-bold text-neutral-900 mb-2">Create Account</h2>
            <p className="text-neutral-500">Join us today</p>
          </div>

          <div className="p-8">
            {message.text && (
              <div
                className={`mb-6 p-4 rounded-lg text-sm font-medium ${
                  message.type === "error"
                    ? "bg-red-50 text-red-700 border border-red-200"
                    : "bg-green-50 text-green-700 border border-green-200"
                }`}
              >
                {message.text}
              </div>
            )}

            <form onSubmit={signUpForm.handleSubmit(handleSignUp)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="signup-firstName"
                    className="block text-sm font-medium text-neutral-700 mb-1"
                  >
                    First Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-4 h-4" />
                    <input
                      id="signup-firstName"
                      type="text"
                      {...signUpForm.register("firstName")}
                      autoComplete="given-name"
                      className="w-full pl-10 pr-3 py-2.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-200 bg-neutral-50 text-neutral-900 placeholder-neutral-400 text-sm"
                      placeholder="First name"
                    />
                  </div>
                  {signUpForm.formState.errors.firstName && (
                    <p className="mt-2 text-sm text-red-600">
                      {signUpForm.formState.errors.firstName.message}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="signup-lastName"
                    className="block text-sm font-medium text-neutral-700 mb-1"
                  >
                    Last Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-4 h-4" />
                    <input
                      id="signup-lastName"
                      type="text"
                      {...signUpForm.register("lastName")}
                      autoComplete="family-name"
                      className="w-full pl-10 pr-3 py-2.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-200 bg-neutral-50 text-neutral-900 placeholder-neutral-400 text-sm"
                      placeholder="Last name"
                    />
                  </div>
                  {signUpForm.formState.errors.lastName && (
                    <p className="mt-2 text-sm text-red-600">
                      {signUpForm.formState.errors.lastName.message}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label
                  htmlFor="signup-email"
                  className="block text-sm font-medium text-neutral-700 mb-1"
                >
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-4 h-4" />
                  <input
                    id="signup-email"
                    type="email"
                    {...signUpForm.register("email")}
                    autoComplete="email"
                    className="w-full pl-10 pr-3 py-2.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-200 bg-neutral-50 text-neutral-900 placeholder-neutral-400 text-sm"
                    placeholder="Enter your email"
                  />
                </div>
                {signUpForm.formState.errors.email && (
                  <p className="mt-2 text-sm text-red-600">
                    {signUpForm.formState.errors.email.message}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="signup-phone"
                  className="block text-sm font-medium text-neutral-700 mb-1"
                >
                  Phone Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-4 h-4" />
                  <input
                    id="signup-phone"
                    type="tel"
                    {...signUpForm.register("phone")}
                    autoComplete="tel"
                    className="w-full pl-10 pr-3 py-2.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-200 bg-neutral-50 text-neutral-900 placeholder-neutral-400 text-sm"
                    placeholder="Enter your phone"
                  />
                </div>
                {signUpForm.formState.errors.phone && (
                  <p className="mt-2 text-sm text-red-600">
                    {signUpForm.formState.errors.phone.message}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="signup-password"
                  className="block text-sm font-medium text-neutral-700 mb-1"
                >
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-4 h-4" />
                  <input
                    id="signup-password"
                    type={showSignUpPassword ? "text" : "password"}
                    {...signUpForm.register("password")}
                    autoComplete="new-password"
                    className="w-full pl-10 pr-10 py-2.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-200 bg-neutral-50 text-neutral-900 placeholder-neutral-400 text-sm"
                    placeholder="Create password"
                  />
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setShowSignUpPassword(!showSignUpPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                  >
                    {showSignUpPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
                {signUpForm.formState.errors.password && (
                  <p className="mt-2 text-sm text-red-600">
                    {signUpForm.formState.errors.password.message}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="signup-confirmPassword"
                  className="block text-sm font-medium text-neutral-700 mb-1"
                >
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-4 h-4" />
                  <input
                    id="signup-confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    {...signUpForm.register("confirmPassword")}
                    autoComplete="new-password"
                    className="w-full pl-10 pr-10 py-2.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-200 bg-neutral-50 text-neutral-900 placeholder-neutral-400 text-sm"
                    placeholder="Confirm password"
                  />
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
                {signUpForm.formState.errors.confirmPassword && (
                  <p className="mt-2 text-sm text-red-600">
                    {signUpForm.formState.errors.confirmPassword.message}
                  </p>
                )}
              </div>

              <div className="flex flex-col items-start space-y-2">
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    {...signUpForm.register("acceptTerms")}
                    className="w-4 h-4 text-accent border-neutral-300 rounded focus:ring-accent bg-white mt-1"
                  />
                  <label className="ml-2 text-sm text-neutral-500">
                    I agree to the{" "}
                    <button
                      type="button"
                      className="text-accent hover:text-accent-dark font-medium underline"
                      onClick={() => setTermsOpen(true)}
                    >
                      Terms of Service
                    </button>{" "}
                    and{" "}
                    <button
                      type="button"
                      className="text-accent hover:text-accent-dark font-medium underline"
                      onClick={() => setPrivacyOpen(true)}
                    >
                      Privacy Policy
                    </button>
                  </label>
                </div>
                {signUpForm.formState.errors.acceptTerms && (
                  <p className="text-sm text-red-600">
                    {signUpForm.formState.errors.acceptTerms.message}
                  </p>
                )}

                <Modal
                  isOpen={isTermsOpen}
                  onClose={() => setTermsOpen(false)}
                  title="Terms of Service"
                >
                  <p>
                    Welcome to our website. By using this service, you agree to
                    the following terms...
                  </p>
                </Modal>

                <Modal
                  isOpen={isPrivacyOpen}
                  onClose={() => setPrivacyOpen(false)}
                  title="Privacy Policy"
                >
                  <p>
                    Your privacy is important to us. We collect data to improve
                    our services...
                  </p>
                </Modal>
              </div>

              <button
                type="submit"
                disabled={
                  loading ||
                  !signUpForm.watch("firstName")?.trim() ||
                  !signUpForm.watch("lastName")?.trim() ||
                  !signUpForm.watch("email")?.trim() ||
                  !signUpForm.watch("phone")?.trim() ||
                  !signUpForm.watch("password") ||
                  !signUpForm.watch("confirmPassword") ||
                  !signUpForm.watch("acceptTerms")
                }
                className="w-full bg-primary text-white py-3 rounded-lg font-semibold hover:bg-primary-light focus:ring-4 focus:ring-primary/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Creating Account..." : "Create Account"}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-neutral-500">
              Already have an account?{" "}
              <button
                type="button"
                onClick={() => setCurrentPage("signin")}
                className="text-accent hover:text-accent-dark font-medium"
              >
                Sign in
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Auth;
